/**
 * JSON serialization with support for `Uint8Array`, `Map` and `Set`, to be able
 * to persist local-first (localStorage) the chat crypto state (device
 * identity, session ratchets, sender-keys) without losing the bytes.
 *
 * Zero-knowledge: ALL of this lives ONLY on the client. The plaintext content and
 * private keys never leave the device; the relay only sees opaque envelopes.
 */

interface TaggedU8 {
  readonly __t: "u8";
  readonly b64: string;
}
interface TaggedMap {
  readonly __t: "map";
  readonly e: [unknown, unknown][];
}
interface TaggedSet {
  readonly __t: "set";
  readonly e: unknown[];
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) {
    binary += String.fromCharCode(b);
  }
  return btoa(binary);
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}

function replacer(this: Record<string, unknown>, key: string, _value: unknown): unknown {
  // `this[key]` is the ORIGINAL value (before the toJSON JSON applies to
  // Uint8Array, which would turn it into an index→byte object). Thus we preserve the bytes.
  const original = this[key];
  if (original instanceof Uint8Array) {
    return { __t: "u8", b64: bytesToBase64(original) } satisfies TaggedU8;
  }
  if (original instanceof Map) {
    return { __t: "map", e: [...original.entries()] } satisfies TaggedMap;
  }
  if (original instanceof Set) {
    return { __t: "set", e: [...original.values()] } satisfies TaggedSet;
  }
  return _value;
}

function reviver(_key: string, value: unknown): unknown {
  if (typeof value !== "object" || value === null || !("__t" in value)) {
    return value;
  }
  if (value.__t === "u8" && "b64" in value && typeof value.b64 === "string") {
    return base64ToBytes(value.b64);
  }
  if (value.__t === "map" && "e" in value && Array.isArray(value.e)) {
    return new Map(value.e);
  }
  if (value.__t === "set" && "e" in value && Array.isArray(value.e)) {
    return new Set(value.e);
  }
  return value;
}

export function encode(value: unknown): string {
  return JSON.stringify(value, replacer);
}

/** Deserializes a blob (revives Uint8Array/Map/Set). The caller knows the type. */
export function decode(text: string): unknown {
  return JSON.parse(text, reviver);
}
